import { filter, Observable, Subject } from 'rxjs';
import { computed, ref, watch, type ComputedRef, type InjectionKey, type Ref } from 'vue';
import type { ModalWidth } from '../components/GenericModal/modalWidth';

let modalInstanceCounter = 0;

/**
 * What a peek anchors to. A real element is the common case; a virtual element
 * (rect snapshot, à la Floating UI) lets the anchor survive its source element
 * being detached from the DOM — e.g. a re-render between click and open. The
 * optional `contextElement` carries the originating element for identity.
 */
export interface PeekAnchor {
  getBoundingClientRect: () => DOMRect;
  contextElement?: HTMLElement | null;
}

/**
 * What the call that opened a modal gets back: how it was closed, and what the
 * dialogue handed over. Only ever this modal's own close.
 */
export interface ModalAnswer<
  TKey extends keyof TMap,
  TMap extends Record<string, { props: any; modalResult: any }>,
> {
  action: 'cancel' | 'confirm';
  modalResult?: TMap[TKey]['modalResult'];
}

export class Modal<
  TKey extends keyof TMap,
  TMap extends Record<string, { props: any; modalResult: any }>,
> {
  closing = false;
  /** Stable identity so views can key the stack without index reuse. */
  readonly id: string;
  /** Settled by this modal's first close; what `openModal` returned. */
  readonly answer: Promise<ModalAnswer<TKey, TMap>>;
  /** Set once `answer` has settled — a later close of this modal is an echo. */
  settled = false;
  /**
   * While set, a close puts the modal away without settling `answer`: the
   * dialogue has promised a later close that will (see `holdAnswer`).
   */
  held = false;
  settle!: (answer: ModalAnswer<TKey, TMap>) => void;

  constructor(
    public readonly modalType: TKey,
    public readonly props: TMap[TKey]['props'],
    /**
     * Optional anchor this modal is tied to. When set, a host may present the
     * modal as an anchored popover ("peek") instead of a centered dialog.
     * Per-instance, so each open carries its own anchor with no shared state.
     */
    public readonly anchorEl: HTMLElement | PeekAnchor | null = null,
  ) {
    modalInstanceCounter += 1;
    this.id = `modal-${modalInstanceCounter}`;
    this.answer = new Promise((resolve) => {
      this.settle = resolve;
    });
  }
}

/**
 * The stack entry a rendered modal belongs to, provided by the host that renders
 * it. A close that names its entry closes that one; without it, a close by type
 * has to guess which of several open modals of that type was meant.
 */
export const ModalEntryKey: InjectionKey<Modal<any, any>> = Symbol('modalEntry');

export interface ModalOpenEvent<
  TKey extends keyof TMap,
  TMap extends Record<string, { props: any; modalResult: any }>,
> {
  type: TKey;
  props?: TMap[TKey]['props'];
  /** Anchor for anchored ("peek") presentation; omit for a normal modal. */
  anchorEl?: HTMLElement | PeekAnchor | null;
}

export interface ModalCloseEvent<
  TKey extends keyof TMap,
  TMap extends Record<string, { props: any; modalResult: any }>,
> {
  type: TKey;
  action: 'cancel' | 'confirm';
  modalResult?: TMap[TKey]['modalResult'];
  /**
   * The entry to close. Hosts and the dialogue chrome pass it (`ModalEntryKey`);
   * left out, the topmost open modal of `type` closes — the one on screen. On
   * the way out it is always set, to the entry that closed.
   */
  modal?: Modal<TKey, TMap>;
}

/** Where an anchored peek sits relative to its anchor. Mirrors floating-vue. */
export type PeekPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end';

/**
 * How a modal type presents itself when it is opened with an anchor. Per type
 * rather than per open: the geometry follows from what the dialogue *is* — a
 * detail popover hangs beside its card, a confirmation drops under the button
 * that raised it — not from which call site opened it.
 */
export interface PeekPresentation {
  /** Defaults to `right-start`, the appointment peek's side-by-side placement. */
  placement?: PeekPlacement;
}

export interface ModalComponent<
  TKey extends keyof TMap,
  TMap extends Record<string, { props: any; modalResult: any }>,
> {
  type: TKey;
  component: () => any;
  /**
   * Which step of the width scale this dialogue occupies. One knob for both
   * presentations — a type that peeks on desktop and sheets on mobile cannot
   * disagree with itself — and a property of the type, not of the call that
   * opened it. Defaults to `sm`; see {@link ModalWidth} for what each step is
   * for and why widening is an opt-in.
   */
  width?: ModalWidth;
  /** Anchored-presentation geometry; only read when the open carries an anchor. */
  peek?: PeekPresentation;
}

export interface ModalComposable<TMap extends Record<string, { props: any; modalResult: any }>> {
  modalStack: Ref<Modal<keyof TMap, TMap>[]>;
  anyModalOpen: ComputedRef<boolean>;
  isModalOpen: (modalType: keyof TMap) => boolean;
  getModal: (modalType: keyof TMap) => Modal<keyof TMap, TMap> | undefined;
  /**
   * Open a modal, and get back the answer of ITS close — never a sibling's of the
   * same type. Settles once, also after the caller unmounts; never rejects.
   */
  openModal: <T extends keyof TMap>(
    event: ModalOpenEvent<T, TMap>,
  ) => Promise<ModalAnswer<T, TMap>>;
  closeModal: <T extends keyof TMap>(event: ModalCloseEvent<T, TMap>) => void;
  /**
   * Keep a modal's answer open past its next close: the dialogue has work in
   * flight and will report the outcome with a close of its own. Until then a
   * dismissal (✕, Escape) puts it away but tells the caller nothing — the
   * confirmation that keeps running its action is the case. Returns the release,
   * to call right before that reporting close.
   */
  holdAnswer: (modal: Modal<keyof TMap, TMap>) => () => void;
  closeAllModals: () => void;
  onModalClose: <T extends keyof TMap>(modalType: T) => Observable<ModalCloseEvent<T, TMap>>;
}

export const createModalComposable = <
  TMap extends Record<string, { props: any; modalResult: any }>,
>(): ModalComposable<TMap> => {
  type TKey = keyof TMap;

  const modalStack = ref<Modal<TKey, TMap>[]>([]);
  const closeTrigger: Subject<any> = new Subject<any>();

  const anyModalOpen = computed(() => {
    return modalStack.value.length > 0;
  });

  const isModalOpen = (modalType: TKey) => {
    return modalStack.value
      .filter((s) => !s.closing)
      .map((s) => s.modalType)
      .includes(modalType as any);
  };

  const getModal = (modalType: TKey) => {
    return modalStack.value.find((s) => s.modalType === modalType);
  };

  const openModal = <T extends TKey>(event: ModalOpenEvent<T, TMap>) => {
    const modal = new Modal<T, TMap>(event.type, event.props || null, event.anchorEl ?? null);
    // @ts-expect-error: Type mismatch due to generic constraints
    modalStack.value.push(modal);
    return modal.answer;
  };

  // The one on screen: a modal of the same type further down is covered by it,
  // and one already closing has been answered.
  const topmostOpen = (modalType: TKey) =>
    [...modalStack.value].reverse().find((s) => s.modalType === modalType && !s.closing);

  const closeModal = <T extends TKey>(event: ModalCloseEvent<T, TMap>) => {
    const modal = (event.modal as Modal<TKey, TMap> | undefined) ?? topmostOpen(event.type);
    if (!modal) {
      // Nothing of that type is open; the event still goes to `onModalClose`, as
      // it always has.
      closeTrigger.next(event);
      return;
    }
    // A second close of an answered modal — the renderer echoing a store-driven
    // close back, a ✕ racing a save — is the same close, so it is nobody's news.
    if (modal.settled) return;
    if (!modal.closing) {
      modal.closing = true;
      setTimeout(() => {
        modalStack.value = modalStack.value.filter((stackModal) => !stackModal.closing);
      }, 200);
    }
    closeTrigger.next({ ...event, modal });
    if (modal.held) return;
    modal.settled = true;
    modal.settle({ action: event.action, modalResult: event.modalResult });
  };

  const holdAnswer = (modal: Modal<TKey, TMap>) => {
    modal.held = true;
    return () => {
      modal.held = false;
    };
  };

  const closeAllModals = () => {
    modalStack.value.forEach((modal) =>
      closeModal({
        type: modal.modalType as TKey,
        action: 'cancel',
        modal: modal as Modal<TKey, TMap>,
      }),
    );
  };

  const onModalClose = <T extends TKey>(modalType: T): Observable<ModalCloseEvent<T, TMap>> => {
    return closeTrigger.asObservable().pipe(filter((event) => event.type === modalType));
  };

  const modalOpen = computed(() => anyModalOpen.value);

  watch(modalOpen, (modalOpen2) => {
    document.getElementsByTagName('body')[0].style.position = modalOpen2 ? 'fixed' : 'relative';
    document.getElementsByTagName('body')[0].style.top = modalOpen2 ? '0' : 'inherit';
    document.getElementsByTagName('body')[0].style.left = modalOpen2 ? '0' : 'inherit';
    document.getElementsByTagName('body')[0].style.right = modalOpen2 ? '0' : 'inherit';
    document.getElementsByTagName('body')[0].style.bottom = modalOpen2 ? '0' : 'inherit';
  });

  return {
    modalStack: modalStack as Ref<Modal<keyof TMap, TMap>[]>,
    anyModalOpen,
    isModalOpen,
    getModal: getModal as (modalType: keyof TMap) => Modal<keyof TMap, TMap> | undefined,
    openModal,
    closeModal,
    holdAnswer,
    closeAllModals,
    onModalClose,
  };
};
